import React from "react";
import { Radio, TreeSelect, DatePicker } from "antd";
import LabelAndValue from "../../custom/form/LabelAndValue";
import Checkbox from "../../custom/input/checkbox/Checkbox";
import HSelect from "../../custom/input/select/Select";
import { CHANNEL_FILTER_TYPE } from "./config/filterFieldConfig";
const { SHOW_PARENT } = TreeSelect;
const { RangePicker } = DatePicker;

/**
 * Renders the appropriate UI for a filter field based on its uiComponent from filterFieldConfig.
 * Add new cases when introducing a new UI component type; existing types work for any new field in config.
 *
 * @param {Object} params
 * @param {Object} params.field - Field config (fieldId, displayName, fieldName, uiComponent)
 * @param {*} params.value - Current value from state
 * @param {Function} params.onChange - (newValue) => void
 * @param {Array|Object|null} params.options - From getOptionsForField(field, context)
 * @param {Object} params.context - Optional { dayjs } for date range
 */
export const renderFilterFieldByUiComponent = ({ field, value, onChange, options = [], context = {} }) => {
    const { fieldId, displayName, title} = field;
    const label = displayName;
    const titleName = title ?? displayName;
    const uiProps = field.uiProps ?? {};

    switch (field.uiComponent) {
        case "TREESELECT": {
            const services = Array.isArray(options) ? options : [];
            const channelFilterType = context.channelFilterType ?? CHANNEL_FILTER_TYPE.CHANNEL;
            const onChannelFilterTypeChange = context.onChannelFilterTypeChange ?? (() => {});
            const isChannelTeamField = field.fieldName === "rightServiceId";
            const channelFilterTypeOptions = field.channelFilterTypeOptions ?? [
                { label: "Channels", value: CHANNEL_FILTER_TYPE.CHANNEL },
                { label: "Teams", value: CHANNEL_FILTER_TYPE.TEAM },
            ];
            const channelFilterTypeGroupLabel = field.channelFilterTypeGroupLabel ?? "Channel or Team";
            const placeholderForMode =
                isChannelTeamField && channelFilterType === CHANNEL_FILTER_TYPE.TEAM
                    ? "Select Teams"
                    : (uiProps.placeholder ?? "All Channels");

            const teamsNode = services.find((s) => s.value === "teams");
            const teamItems = teamsNode?.children || [];
            const teamSelectOptions = teamItems.map((team) => ({
                label: team.teamName ?? team.title,
                value: team.value,
            }));
            const channelServices = isChannelTeamField
                ? services.filter((s) => s.value !== "All" && s.value !== "teams")
                : services.filter((s) => s.value !== "All");
            const treeData = channelServices.map((service) => ({
                ...service,
                title: (
                    <span
                        data-cy={`inbox-filter-option-${String(service.title ?? "")
                            .toLowerCase()
                            .replace(/ /g, "_")}`}
                    >
                        {service.title}
                    </span>
                ),
                children: service.children?.map((child) => ({
                    ...child,
                    title: (
                        <span
                            className={/^\+?\d+$/.test(child.title) ? "connected-number" : ""}
                            data-cy="connected-number"
                        >
                            {child.title}
                        </span>
                    ),
                })),
            }));
            const currentValue = Array.isArray(value) ? value : [];

            const treeSelectNode = (
                <TreeSelect
                    tabIndex={2}
                    treeCheckable
                    showCheckedStrategy={SHOW_PARENT}
                    suffixIcon={<div className="actionIconsSprite fromNumberDropDownIcon" />}
                    placeholder={placeholderForMode}
                    value={value ?? []}
                    switcherIcon={({ expanded }) => (
                        <div
                            data-cy="filter-inside-right-icon"
                            className={`actionIconsSprite click-to-switcher-right-icon ${expanded ? "click-to-switcher-right-icon-rotate-down" : ""}`}
                        />
                    )}
                    placement="bottomLeft"
                    dropdownStyle={{ width: 180, maxHeight: 256, overflowY: "auto" }}
                    listHeight={256}
                    popupClassName="inbox-filter-channel-dropdown"
                    popupMatchSelectWidth={false}
                    data-cy="inbox-filter"
                    showSearch
                    treeNodeFilterProp="title"
                    filterTreeNode={(inputValue, treeNode) => {
                        const title = String(treeNode.title?.props?.children ?? treeNode.title ?? "");
                        const friendlyName = String(treeNode.friendlyName ?? "");
                        const teamName = String(treeNode.teamName ?? "");
                        const phoneNumber = String(treeNode.phoneNumber ?? "");
                        const searchText = (inputValue ?? "").toLowerCase();
                        return (
                            title.toLowerCase().includes(searchText) ||
                            friendlyName.toLowerCase().includes(searchText) ||
                            teamName.toLowerCase().includes(searchText) ||
                            phoneNumber.toLowerCase().includes(searchText)
                        );
                    }}
                    onChange={(v) => onChange(v ?? [])}
                    treeData={treeData}
                    className={isChannelTeamField ? "h-100  flex-grow-1 border-0" : "h-100 w-auto"}
                    rootClassName="channel-dropdown overflow-hidden"
                />
            );

            const teamSelectNode = (
                <HSelect
                    bordered={false}
                    variant="borderless"
                    mode="multiple"
                    size={"small"}
                    customProps={{
                        value: currentValue,
                        onChange: (v) => onChange(Array.isArray(v) ? v : []),
                        className: "overflow-hidden",
                        showSearch: true,
                    }}
                    defaultTagRender={{
                        onCloseSetState: (v) => {
                            const next = typeof v === "function" ? v(currentValue) : Array.isArray(v) ? v : [];
                            onChange(Array.isArray(next) ? next : []);
                        },
                        tagItemToRender: "label",
                    }}
                    popupMatchSelectWidth={false}
                    placeholder={placeholderForMode}
                    options={teamSelectOptions}
                    loading={false}
                    rootClassName={
                        isChannelTeamField ? "assignee-dropdown inbox-filter-channel-team-select" : "assignee-dropdown"
                    }
                />
            );

            if (isChannelTeamField) {
                const selector = channelFilterType === CHANNEL_FILTER_TYPE.TEAM ? teamSelectNode : treeSelectNode;
                return (
                    <div key={fieldId} className="inbox-filter-channel-or-team-wrapper">
                        <LabelAndValue label={channelFilterTypeGroupLabel}>
                            <div className="inbox-filter-channel-or-team-container">
                                <Radio.Group
                                    value={channelFilterType}
                                    onChange={(e) => onChannelFilterTypeChange(e.target.value)}
                                    optionType="button"
                                    options={channelFilterTypeOptions}
                                    className="inbox-filter-channel-team-radio"
                                />
                                <div className="inbox-filter-channel-team-divider" />
                                <div className="inbox-filter-channel-team-select-wrapper">{selector}</div>
                            </div>
                        </LabelAndValue>
                    </div>
                );
            }

            return (
                <div key={fieldId}>
                    <LabelAndValue label={label}>{treeSelectNode}</LabelAndValue>
                </div>
            );
        }

        case "CHECKBOX": {
            const checkedVal = uiProps.checkedValue ?? "unread";
            const uncheckedVal = uiProps.uncheckedValue ?? "all";
            const option =
                Array.isArray(options) && options.length > 0 ? options[0] : { label: displayName, value: checkedVal };
            return (
                <div key={fieldId}>
                    <LabelAndValue label={titleName}>
                        <Checkbox
                            data-cy={uiProps.dataCy ?? "inbox-filter-status-unread"}
                            checked={value === checkedVal}
                            value={value}
                            item={option}
                            onChange={(e) => onChange(e.target.checked ? checkedVal : uncheckedVal)}
                        />
                    </LabelAndValue>
                </div>
            );
        }

        case "MULTI_SELECT": {
            const items = Array.isArray(options) ? options : [];
            return (
                <div key={fieldId}>
                    <LabelAndValue label={label}>
                        <div className="d-flex gap-4 w-auto flex-wrap">
                            {items.map((item) => (
                                <Checkbox
                                    key={item.value ?? item.key}
                                    data-cy={`inbox-filter-flag-${item.color ?? item.value}`}
                                    rounded={false}
                                    checked={Array.isArray(value) && value.includes(item.value ?? item.color)}
                                    item={{ label: item.label ?? item.value, value: item.value ?? item.color }}
                                    onChange={() => {
                                        const v = item.value ?? item.color;
                                        const next = Array.isArray(value)
                                            ? value.includes(v)
                                                ? value.filter((c) => c !== v)
                                                : [...value, v]
                                            : [v];
                                        onChange(next);
                                    }}
                                />
                            ))}
                        </div>
                    </LabelAndValue>
                </div>
            );
        }

        case "DATE_RANGE": {
            const dayjs = context.dayjs;
            return (
                <div key={fieldId}>
                    <LabelAndValue label={label}>
                        <RangePicker
                            data-cy="inbox-filter-date-range"
                            format="YYYY-MM-DD"
                            value={value ?? null}
                            onChange={onChange}
                            disabledDate={(current) => current && dayjs && current > dayjs().endOf("day")}
                            className="w-100"
                            rootClassName="date-range-picker"
                            popupClassName="inbox-filter-date-picker-dropdown"
                        />
                    </LabelAndValue>
                </div>
            );
        }

        case "ASSIGNEE_PICKER": {
            const selectOptions = Array.isArray(options) ? options : [];
            const assigneeValue = Array.isArray(value) ? value : [];
            return (
                <div key={fieldId} className="overflow-auto px-1">
                    <LabelAndValue label={label}>
                        <HSelect
                            data-cy="inbox-filter-assigned-users"
                            bordered={false}
                            mode="multiple"
                            size="medium"
                            groupBy="group"
                            customProps={{
                                value: assigneeValue,
                                onChange: (v) => onChange(Array.isArray(v) ? v : []),
                                className: "overflow-hidden",
                                showSearch: true,
                            }}
                            defaultTagRender={{
                                onCloseSetState: (v) => {
                                    const next = typeof v === "function" ? v(assigneeValue) : Array.isArray(v) ? v : [];
                                    onChange(Array.isArray(next) ? next : []);
                                },
                                tagItemToRender: "label",
                            }}
                            popupMatchSelectWidth={false}
                            placeholder={uiProps.placeholder ?? ""}
                            options={selectOptions}
                            loading={false}
                            rootClassName="assignee-dropdown"
                        />
                    </LabelAndValue>
                </div>
            );
        }

        default:
            return null;
    }
};

